'use client';
import { DURATION } from '@/features/truncated-text-content/utils/consts';
import { Button } from '@/shared/ui/Button';
import { twMergeExt } from '@/shared/utils/twMergeExt';
import { cva } from 'class-variance-authority';
import { motion, useMotionValue } from 'motion/react';
import { FC, ReactNode, useState } from 'react';
import useMeasure from 'react-use-measure';
import ArrowDownIcon from '~/icons/arrow-down.svg';

interface Props {
  className?: string;
  children?: ReactNode;
  lines?: number;
  dangerouslySetInnerHTML?: { __html: string };
  onExpandTrigger?: (expanded: boolean) => void;
}

export const TruncatedTextContent: FC<Props> = ({
  className,
  children,
  lines = 5,
  dangerouslySetInnerHTML,
  onExpandTrigger,
}) => {
  const [placeholderMeasureRef, placeholderMeasure] = useMeasure();

  const [isTruncated, setIsTruncated] = useState(true);
  const lineClamp = useMotionValue<number | string>(lines);

  const onExpand = () => {
    setIsTruncated((prev) => {
      prev ? lineClamp.set('initial') : setTimeout(() => lineClamp.set(lines), DURATION);
      onExpandTrigger && onExpandTrigger(!prev);
      return !prev;
    });
  };

  return (
    <div className={twMergeExt(cvaRoot(), className)}>
      <div
        ref={placeholderMeasureRef}
        className={cvaTruncatedWrapper({
          isPlaceholder: true,
        })}
        style={{
          WebkitLineClamp: isTruncated ? lines : 'initial',
        }}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      >
        {children}
      </div>

      <motion.div
        className={cvaTruncatedWrapper()}
        initial={false}
        animate={{
          height: placeholderMeasure.height || 'auto',
        }}
        transition={transition}
        style={{ WebkitLineClamp: lineClamp }}
        dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      >
        {children}
      </motion.div>

      <Button
        className={cvaButton()}
        contentWrapperClassName={'flex items-center gap-0.2'}
        hoverEffect={'color'}
        onClick={onExpand}
      >
        <div className={cvaButtonText()}>{isTruncated ? 'Развернуть' : 'Свернуть'}</div>
        <motion.div
          initial={false}
          animate={{
            rotateZ: isTruncated ? '0deg' : '180deg',
          }}
          transition={transition}
        >
          <ArrowDownIcon className={cvaButtonIcon()} />
        </motion.div>
      </Button>
    </div>
  );
};

const transition = {
  duration: DURATION / 1000,
  ease: 'easeOut',
};

const cvaRoot = cva(['TruncatedTextContent-cvaRoot', 'relative']);

const cvaTruncatedWrapper = cva(['TruncatedTextContent-cvaTruncatedWrapper', 'overflow-hidden', 'line-clamp-5'], {
  variants: {
    isPlaceholder: {
      true: 'absolute top-0 left-0 w-full invisible',
      false: '',
    },
  },
});

const cvaButton = cva([
  'TruncatedTextContent-cvaButton group/TranslateAnimation',
  'mt-3.2',
  'text-bodyMSemibold text-cBlack',
]);

const cvaButtonText = cva(['TruncatedTextContent-cvaButtonText', 'border-b border-dashed border-cBlack32']);

const cvaButtonIcon = cva(['TruncatedTextContent-cvaButtonIcon', 'w-1.6 h-1.6']);
